import { useState, createContext, useMemo, useCallback, useEffect, useRef } from "react";
import { useMsal } from "@azure/msal-react";
import { useGraphToken } from "../api/index";
import { checkNewNews, markNewsSeen } from "../api/index";

const STORAGE_KEY_PREFIX = "news_notification_";

export const NewsNotificationContext = createContext(null);

export const NewsNotificationProvider = ({ children }) => {
  const [hasNewNews, setHasNewNews] = useState(false);
  const [newNewsCount, setNewNewsCount] = useState(0);
  const [newNewsIds, setNewNewsIds] = useState([]);
  const { accounts } = useMsal();
  const { getAccessToken } = useGraphToken();

  const accountsRef = useRef(accounts);
  accountsRef.current = accounts;
  const getAccessTokenRef = useRef(getAccessToken);
  getAccessTokenRef.current = getAccessToken;

  // Initialize from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY_PREFIX + "state");
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        setHasNewNews(parsed.hasNewNews || false);
        setNewNewsCount(parsed.newNewsCount || 0);
        setNewNewsIds(parsed.newNewsIds || []);
      } catch (e) {
        // Ignore parse errors
      }
    }
  }, []);

  // Persist to localStorage when state changes
  useEffect(() => {
    localStorage.setItem(
      STORAGE_KEY_PREFIX + "state",
      JSON.stringify({ hasNewNews, newNewsCount, newNewsIds })
    );
  }, [hasNewNews, newNewsCount, newNewsIds]);

  const checkForNewNews = useCallback(
    async (newsItems, locationId) => {
      const currentAccounts = accountsRef.current;

      console.log("[checkForNewNews] called", {
        hasAccount: !!currentAccounts[0],
        newsItemsCount: newsItems?.length,
      });

      console.log("[NewsNotificationContext] checkForNewNews called", {
        hasAccount: !!currentAccounts[0],
        newsItemsLength: newsItems?.length,
        locationId,
      });

      if (!currentAccounts[0] || !newsItems || newsItems.length === 0 || !locationId) {
        console.log("[NewsNotificationContext] guard clause hit", {
          noAccount: !currentAccounts[0],
          noNewsItems: !newsItems,
          emptyNewsItems: newsItems?.length === 0,
          noLocationId: !locationId,
        });
        return;
      }

      const userId = currentAccounts[0].localAccountId;
      const currentNewsIds = newsItems.map((item) => item.id).filter(Boolean);

      console.log("[checkForNewNews] newsIds extracted", {
        total: newsItems.length,
        withValidId: currentNewsIds.length,
        sampleIds: currentNewsIds.slice(0, 3),
      });
      console.log("[NewsNotificationContext] newsIds after filter", {
        totalItems: newsItems.length,
        validIds: currentNewsIds.length,
        ids: currentNewsIds,
      });

      if (currentNewsIds.length === 0) {
        console.log("[NewsNotificationContext] all IDs were falsy, skipping");
        return;
      }

      try {
        const accessToken = await getAccessTokenRef.current();
        console.log("[NewsNotificationContext] token acquired", { hasToken: !!accessToken });
        if (!accessToken) {
          console.warn("[checkForNewNews] no access token");
          return;
        }

        console.log("[checkForNewNews] calling backend API");

        console.log("[NewsNotificationContext] calling checkNewNews API", {
          userId,
          locationId: locationId.toString(),
          currentNewsIdsCount: currentNewsIds.length,
        });

        const result = await checkNewNews(accessToken, {
          userId,
          locationId: locationId.toString(),
          currentNewsIds,
        });

        console.log("Result:", result);

        if (result) {
          setHasNewNews(result.hasNewNews || false);
          setNewNewsCount(result.newNewsCount || 0);
          setNewNewsIds(result.newNewsIds || []);
        }
      } catch (error) {
        console.warn("Failed to check for new news", error);
      }
    },
    []
  );

  const markAsSeen = useCallback(
    async (newsIds, locationId) => {
      if (!accounts[0] || !newsIds || newsIds.length === 0 || !locationId) {
        return;
      }

      const userId = accounts[0].localAccountId;
      const filteredNewsIds = newsIds.filter(Boolean);

      if (filteredNewsIds.length === 0) {
        return;
      }

      try {
        const accessToken = await getAccessToken();
        if (!accessToken) {
          return;
        }

        await markNewsSeen(accessToken, {
          userId,
          locationId: locationId.toString(),
          newsIds: filteredNewsIds,
        });
      } catch (error) {
        console.warn("Failed to mark news as seen", error);
      }
    },
    [accounts, getAccessToken]
  );

  const clearNotification = useCallback(() => {
    setHasNewNews(false);
    setNewNewsCount(0);
    setNewNewsIds([]);
  }, []);

  const context = useMemo(
    () => ({
      hasNewNews,
      newNewsCount,
      newNewsIds,
      checkForNewNews,
      markAsSeen,
      clearNotification,
    }),
    [hasNewNews, newNewsCount, newNewsIds, checkForNewNews, markAsSeen, clearNotification]
  );

  return (
    <NewsNotificationContext.Provider value={context}>
      {children}
    </NewsNotificationContext.Provider>
  );
};
